import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import LocaleLink from '@/components/LocaleLink';
import MaybeLink from '@/components/MaybeLink';
import { useLocale } from '@/hooks/useLocale';
import { appLoginUrl, appSignupUrl } from '@/config/appLinks';
import { NAV_EN } from '@/copy/nav-en';
import { NAV_AR } from '@/copy/nav-ar';
import { useDrawer } from './useDrawer';
import Drawer from './Drawer';
import IconHamburger from './icons/IconHamburger';
import IconClose from './icons/IconClose';
import IconGlobe from './icons/IconGlobe';
import './TopNav.css';

const DRAWER_ID  = 'mkt-nav-drawer';

/* Nav pins height (not width), so the wider all-caps PRINOTES wordmark grows
 * the logo horizontally rather than changing its height. */
const LOGO_BY_LOCALE = {
  en: '/logos/prinotes-lockup-en-horizontal-colored-on-light.svg',
  ar: '/logos/prinotes-lockup-ar-horizontal-colored-on-light.svg',
} as const;

const LOCALE_LABEL = {
  en: { full: 'العربية', abbr: 'ع' },
  ar: { full: 'English', abbr: 'EN' },
} as const;

export default function TopNav() {
  const { locale, strings } = useLocale();
  const { pathname } = useLocation();
  const copy = locale === 'ar' ? NAV_AR : NAV_EN;
  const [scrolled, setScrolled] = useState(false);
  const drawer = useDrawer();
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let rafId = 0;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        setScrolled(window.scrollY > 8);
        rafId = 0;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  /* Focus trap — active only when drawer is open. Cycles between hamburger
   * (which renders the close-X when isOpen) and the first/last focusable
   * element inside .mkt-drawer__sheet. ~30 LOC per Decisions §D5.
   * Drawer is rendered as a sibling (no portal), so document.querySelector
   * finds it reliably. */
  useEffect(() => {
    if (!drawer.isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const sheet = document.querySelector<HTMLElement>(`#${DRAWER_ID} .mkt-drawer__sheet`);
      const hamburger = hamburgerRef.current;
      if (!sheet || !hamburger) return;
      const focusables = sheet.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      const active = document.activeElement as HTMLElement | null;
      /* Catch-all: focus escaped the trap zone (sheet OR hamburger) — pull it
       * back to first. Brand link, locale switcher, signin, sep, CTA stay
       * focusable above the drawer because the topbar floats z:100 above
       * drawer z:90 (canvas lock — user must be able to click the brand to
       * go home). Without this guard, a Tab from the brand link would walk
       * down into the Hero behind the drawer. */
      if (active && active !== hamburger && !sheet.contains(active)) {
        e.preventDefault();
        first.focus();
        return;
      }
      if (e.shiftKey) {
        if (active === first) { e.preventDefault(); hamburger.focus(); }
        else if (active === hamburger) { e.preventDefault(); last.focus(); }
      } else {
        if (active === last) { e.preventDefault(); hamburger.focus(); }
        else if (active === hamburger) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawer.isOpen]);

  const target = locale === 'ar' ? 'en' : 'ar';
  const swapPath = pathname.replace(/^\/(ar|en)/, `/${target}`);
  const localeText = LOCALE_LABEL[locale];

  return (
    <>
      <nav
        className={`mkt-nav is-translucent${scrolled ? ' is-scrolled' : ''}${drawer.isOpen ? ' is-drawer-open' : ''}`}
        aria-label={strings.nav.home}
      >
        <div className="mkt-nav__inner">
          <LocaleLink to="/" className="mkt-nav__brand" aria-label={strings.nav.home}>
            <img
              src={LOGO_BY_LOCALE[locale]}
              alt={strings.nav.home}
              height={50}
            />
          </LocaleLink>

          <div className="mkt-nav__links">
            {copy.links.map((link) => (
              <MaybeLink key={link.href} to={link.href} live={link.live} external={link.external}>
                {link.label}
              </MaybeLink>
            ))}
          </div>

          <div className="mkt-nav__right">
            <Link
              to={swapPath}
              className="mkt-locale-a"
              hrefLang={target}
              aria-label={localeText.full}
              /* Render globe + label in the TARGET language's reading direction
                 so the globe sits correctly for that word: on the left of an
                 LTR word ("English"), on the right of an RTL word ("العربية"),
                 regardless of the current page direction. */
              dir={target === 'ar' ? 'rtl' : 'ltr'}
            >
              <IconGlobe size={18} className="mkt-locale-a__icon" />
              <span className="mkt-locale-a__label">{localeText.full}</span>
              <span className="mkt-locale-a__abbr" aria-hidden="true">{localeText.abbr}</span>
            </Link>

            {/* Log in + Start free intentionally render WITHOUT target="_blank" — these are
                first-party app navigations (prinotes.com → app.prinotes.com) and should
                keep the user in their existing tab. Matches Phase 0 Hero precedent
                (Hero.tsx:74-79). Cross-origin social links in Footer.tsx open in new tab. */}
            <a href={appLoginUrl(locale)} className="mkt-nav__signin" aria-label={strings.nav.signin}>
              <span className="mkt-nav__signin-text">{strings.nav.signin}</span>
            </a>

            <span className="mkt-nav__sep" aria-hidden="true" />

            <a href={appSignupUrl(locale)} className="mkt-nav__cta">{strings.nav.startFree}</a>
          </div>

          <button
            ref={hamburgerRef}
            type="button"
            className="mkt-nav__hamburger"
            aria-label={drawer.isOpen ? strings.nav.close : strings.nav.menu}
            aria-expanded={drawer.isOpen}
            aria-controls={DRAWER_ID}
            onClick={drawer.toggle}
          >
            {drawer.isOpen ? <IconClose size={22} /> : <IconHamburger size={22} />}
          </button>
        </div>
      </nav>
      <Drawer isOpen={drawer.isOpen} drawerId={DRAWER_ID} />
    </>
  );
}
